import React, { useRef, useState, useMemo, useCallback, useEffect } from 'react';
import html2canvas from 'html2canvas';
import { Download, Copy } from 'lucide-react';
import Button from './Button';
import {
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

interface BarChartProps {
  data: any[];
  xAxis: string;
  yAxis: string;
  title?: string;
  isPrintMode?: boolean;
}

// Custom tooltip to show original value as fraction - memoized
const CustomTooltip = React.memo(
  ({
    active,
    payload,
    yAxis,
    xAxis,
    total,
  }: {
    active?: boolean;
    payload?: any[];
    yAxis: string;
    xAxis: string;
    total: number;
  }) => {
    if (active && payload && payload.length > 0) {
      const item = payload[0].payload;
      const value = item[yAxis];
      const percentValue = item[`${yAxis}Percent`];

      // Check if values are already percentages by checking:
      // 1. If the yAxis field name contains "percent" or "percentage"
      // 2. OR if individual values are in 0-100 range (even if total exceeds 100)
      const fieldNameIsPercent = yAxis.toLowerCase().includes('percent');
      const valueInPercentRange = value >= 0 && value <= 100;
      const isPercentage = fieldNameIsPercent || valueInPercentRange;

      return (
        <div className="bg-white p-2 border rounded shadow text-xs">
          <div>Option: {item[xAxis]}</div>
          {isPercentage ? (
            <div>Value: {value.toFixed(2)}%</div>
          ) : (
            <div>
              Score: {value}/{total} ({percentValue.toFixed(2)}%)
            </div>
          )}
        </div>
      );
    }
    return null;
  },
);

// Custom label for percentage at end of bar - memoized
const BarEndLabel = React.memo((props: any) => {
  const { x, y, width, height, value, isPrintMode: printMode } = props;
  return (
    <text
      x={x + width + 8}
      y={y + (height || 30) / 2}
      fill="#222"
      fontSize={printMode ? 22 : 12}
      textAnchor="start"
      dominantBaseline="central"
    >
      {value.toFixed(1)}%
    </text>
  );
});

// Custom bar shape to color the first 2px as Y-axis color - memoized
const CustomBarShape = React.memo((props: any) => {
  const { x, y, width, height, fill } = props;
  // If width is less than or equal to 2, just use the Y-axis color
  const p = 1;
  if (width <= p) {
    return <rect x={x} y={y} width={width} height={height} fill="#000" />;
  }
  return (
    <g>
      {/* Y-axis color segment */}
      <rect x={x} y={y} width={p} height={height} fill="#000" />
      {/* Main bar */}
      <rect x={x + p} y={y} width={width - p} height={height} fill={fill} />
    </g>
  );
});

const BarChart: React.FC<BarChartProps> = ({ data, xAxis, yAxis, title, isPrintMode = false }) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.clientWidth);
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const isMobile = !isPrintMode && containerWidth > 0 && containerWidth < 500;
  const yAxisWidth = isPrintMode ? 300 : isMobile ? 120 : 200;

  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [cachedImage, setCachedImage] = useState<string | null>(null);
  const [cachedBlob, setCachedBlob] = useState<Blob | null>(null);
  const [showTooltip, setShowTooltip] = useState<{ text: string; y: number } | string | null>(null);

  // Memoize parsed and scaled data
  const scaledData = useMemo(() => {
    return data.map((item) => {
      const scaledItem = { ...item };
      const value = item[yAxis];

      // Handle "X/10" format
      if (typeof value === 'string' && value.includes('/')) {
        const [numerator] = value.split('/');
        scaledItem[yAxis] = Number(numerator);
      }
      // Handle regular numbers
      else if (typeof value === 'number' || !isNaN(Number(value))) {
        scaledItem[yAxis] = Number(value);
      }

      return scaledItem;
    });
  }, [data, yAxis]);

  // Memoize total calculation
  const total = useMemo(() => {
    return scaledData.reduce((acc, item) => acc + item[yAxis], 0);
  }, [scaledData, yAxis]);

  // Memoize percentage data for Y axis
  const percentData = useMemo(() => {
    return scaledData.map((item) => ({
      ...item,
      [`${yAxis}Percent`]: total > 0 ? (item[yAxis] / total) * 100 : 0,
    }));
  }, [scaledData, yAxis, total]);

  // Memoize dynamic X-axis domain calculation
  const xAxisMax = useMemo(() => {
    const maxPercent = Math.max(...percentData.map((item) => item[`${yAxis}Percent`] || 0));
    // Round up to nearest quarter: 25, 50, 75, or 100
    if (maxPercent <= 0) return 25;
    if (maxPercent <= 25) return 25;
    if (maxPercent <= 50) return 50;
    if (maxPercent <= 75) return 75;
    return 100;
  }, [percentData, yAxis]);

  // Memoize dynamic height calculation
  const barRowHeight = isPrintMode ? 80 : isMobile ? 55 : 45;
  const chartHeight = useMemo(() => {
    return Math.min(Math.max(data.length * barRowHeight, 200), 1000);
  }, [data.length, barRowHeight]);

  // Lazy cache image only when needed (download/copy)
  const cacheImage = useCallback(async (): Promise<{ dataUrl: string; blob: Blob } | null> => {
    if (chartRef.current) {
      if (cachedImage && cachedBlob) {
        return { dataUrl: cachedImage, blob: cachedBlob };
      }
      try {
        const canvas = await html2canvas(chartRef.current, {
          backgroundColor: '#fff',
          scale: 2,
          useCORS: true,
          logging: false,
        });
        const dataUrl = canvas.toDataURL('image/png');
        const blob = await new Promise<Blob>((resolve, reject) => {
          canvas.toBlob((b) => {
            if (b) resolve(b);
            else reject(new Error('Failed to create blob'));
          }, 'image/png');
        });
        setCachedImage(dataUrl);
        setCachedBlob(blob);
        return { dataUrl, blob };
      } catch (err) {
        console.error('Failed to cache chart image:', err);
        return null;
      }
    }
    return null;
  }, [cachedImage, cachedBlob]);

  const handleDownload = useCallback(async () => {
    setDownloading(true);
    try {
      const result = await cacheImage();
      if (result?.dataUrl) {
        const link = document.createElement('a');
        link.download = 'chart.png';
        link.href = result.dataUrl;
        link.click();
      }
    } finally {
      setDownloading(false);
    }
  }, [cacheImage]);

  const handleCopy = useCallback(async () => {
    try {
      const result = await cacheImage();
      if (result?.blob) {
        await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': result.blob })]);
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      }
    } catch (err) {
      // fallback: do nothing
    }
  }, [cacheImage]);

  return (
    <div className="w-full relative" ref={containerRef}>
      {title && (
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-medium text-gray-700">{title}</h3>
          <div className="flex gap-2 no-print">
            <div className="relative">
              <Button
                size="sm"
                variant="ghost"
                icon={<Download className="h-4 w-4" />}
                onClick={handleDownload}
                disabled={downloading}
                title="Download chart as image"
                onMouseEnter={() => setShowTooltip('download')}
                onMouseLeave={() => setShowTooltip(null)}
              >
                {''}
              </Button>
              {showTooltip === 'download' && (
                <div className="absolute right-0 mt-2 bg-gray-800 text-white text-xs px-2 py-1 rounded z-20 whitespace-nowrap">
                  Download chart as image
                </div>
              )}
            </div>
            <div className="relative">
              <Button
                size="sm"
                variant="ghost"
                icon={<Copy className="h-4 w-4" />}
                onClick={handleCopy}
                title="Copy chart as image"
                onMouseEnter={() => setShowTooltip('copy')}
                onMouseLeave={() => setShowTooltip(null)}
              >
                {''}
              </Button>
              {showTooltip === 'copy' && (
                <div className="absolute right-0 mt-2 bg-gray-800 text-white text-xs px-2 py-1 rounded z-20 whitespace-nowrap">
                  Copy chart as image
                </div>
              )}
            </div>
            {copied && <span className="text-green-600 text-xs ml-1">Copied!</span>}
          </div>
        </div>
      )}

      <div ref={chartRef} className="w-full relative" style={{ height: `${chartHeight}px` }}>
        {/* Label tooltip */}
        {showTooltip && typeof showTooltip === 'object' && (
          <div
            className="absolute left-0 bg-gray-800 text-white text-xs px-2 py-1 rounded z-30 max-w-xs break-words pointer-events-none whitespace-nowrap"
            style={{ top: `${showTooltip.y + 8}px` }}
          >
            {showTooltip.text}
          </div>
        )}
        <ResponsiveContainer width="100%" height={chartHeight}>
          <RechartsBarChart
            data={percentData}
            layout="vertical"
            margin={{
              top: 0,
              right: isMobile ? 40 : 52,
              left: 0,
              bottom: 0,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              type="number"
              domain={[0, xAxisMax]}
              tickFormatter={(value) => `${value}%`}
              fontSize={12}
              axisLine={{ stroke: '#000', strokeWidth: 2 }}
              // horizontal axis is now percentage, dynamically scaled to data range
            />
            <YAxis
              type="category"
              dataKey={xAxis}
              width={yAxisWidth}
              fontSize={12}
              stroke="#000"
              strokeWidth={2}
              interval={0}
              tick={({ x, y, payload }: any) => {
                const label = payload.value;
                const labelHeight = isPrintMode ? 60 : isMobile ? 40 : 32;
                const fontSize = isPrintMode ? 22 : isMobile ? 11 : 12;
                return (
                  <g transform={`translate(${x},${y})`}>
                    <foreignObject
                      x={-(yAxisWidth - 20)}
                      y={-(labelHeight / 2)}
                      width={yAxisWidth - 24}
                      height={labelHeight}
                    >
                      <div
                        onMouseEnter={() => setShowTooltip({ text: label, y })}
                        onMouseLeave={() => setShowTooltip(null)}
                        style={{
                          fontSize,
                          color: '#666',
                          textAlign: 'right',
                          wordBreak: 'break-word',
                          whiteSpace: 'normal',
                          lineHeight: '1.2',
                          height: labelHeight,
                          overflow: 'hidden',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-end',
                          cursor: 'help',
                        }}
                      >
                        {label}
                      </div>
                    </foreignObject>
                  </g>
                );
              }}
            />
            {/* Tooltip is interactive/hover only, so add no-print class */}
            <Tooltip
              wrapperStyle={{ pointerEvents: 'auto' }}
              content={useMemo(
                () => (
                  <CustomTooltip yAxis={yAxis} xAxis={xAxis} total={total} />
                ),
                [yAxis, xAxis, total],
              )}
            />
            {/* <Legend /> */}
            <Bar
              dataKey={`${yAxis}Percent`}
              fill="#03e8d3"
              barSize={isPrintMode ? 45 : 30}
              label={useMemo(
                () => (
                  <BarEndLabel isPrintMode={isPrintMode} />
                ),
                [isPrintMode],
              )}
              shape={useMemo(
                () => (
                  <CustomBarShape />
                ),
                [],
              )}
            />
          </RechartsBarChart>
        </ResponsiveContainer>
        {/* Overlay custom Y axis line on top of bars */}
        {/* <svg className="pointer-events-none absolute top-10 left-0" width="100%" height="100%" style={{zIndex: 10}}>
          <line x1="120" y1="0" x2="120" y2="93%" stroke="#000" strokeWidth="2" />
        </svg> */}
      </div>
    </div>
  );
};

export default BarChart;
